import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

// --- parsing: integer, a/b, decimal (comma or dot)
function parseFraction(raw) {
  if (raw == null) return null;
  const s0 = String(raw).trim();
  if (!s0) return null;
  const s = s0.replace(",", ".");
  if (/^[+-]?\d+$/.test(s)) return [parseInt(s, 10), 1];
  const m = s.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) { const den = parseInt(m[2], 10); if (den === 0) return null; return [parseInt(m[1], 10), den]; }
  const d = s.match(/^[+-]?(?:\d+|\d*\.\d+)$/);
  if (d) {
    const sign = s.startsWith("-") ? -1 : 1;
    const body = s.replace(/^[+-]/, "");
    const [ip, fp=""] = body.split(".");
    const k = fp.length;
    const p = sign * (parseInt(ip || "0", 10) * (10 ** k) + parseInt(fp || "0", 10));
    const q = 10 ** k;
    return [p, q];
  }
  return null;
}

// --- helpers
function primeFactorization(x) {
  const fac = {};
  let n = x;
  let d = 2;
  while (d * d <= n) {
    while (n % d === 0) {
      fac[d] = (fac[d] || 0) + 1;
      n /= d;
    }
    d += (d === 2 ? 1 : 2); // 2,3,5,7...
  }
  if (n > 1) fac[n] = (fac[n] || 0) + 1;
  return fac; // {prime: exponent}
}
function powInt(base, exp) {
  let r = 1;
  for (let i = 0; i < exp; i++) r *= base;
  return r;
}

// ========== ŚREDNIA GEOMETRYCZNA — CAŁKOWITY WYNIK, liczby różne (max x2) ==========
export function generateTask(options = {}) {
  if (options.basic) {
    const variants = [
      { arr: [2, 8], g: 4 },
      { arr: [3, 12], g: 6 },
      { arr: [1, 4, 16], g: 4 },
      { arr: [2, 8, 32], g: 8 },
      { arr: [1, 8, 64], g: 8 },
    ];
    const selected = variants[Math.floor(Math.random() * variants.length)];
    const arr = [...selected.arr];
    const n = arr.length;
    const prod = arr.reduce((result, value) => result * value, 1);

    return {
      type: "MeanGeometricRawTask",
      data: { n, arr, prod, g: selected.g, k: selected.g },
      uid: `mean_geo_basic_${Date.now()}_${Math.floor(Math.random() * 1e9)}`,
    };
  }

  const n = 3 + Math.floor(Math.random()*3); // 3..5
  const k = 3 + Math.floor(Math.random()*18); // 3..20
  const prodTarget = powInt(k, n); // k^n

  const fac = primeFactorization(k); // k = ∏ p^{e_p}
  const primes = Object.keys(fac).map(s => parseInt(s, 10));

  let arr = null;

  // spróbuj kilka razy wylosować rozkład, który spełnia warunki
  for (let attempt = 0; attempt < 80; attempt++) {
    // rozdzielamy wykładniki (e_p * n) po kubełkach 0..n tak, aby powstało dużo różnych liczb
    const bucketsByPrime = {};
    for (const p of primes) {
      const totalExp = fac[p] * n;      // w k^n
      const buckets = Array(n).fill(0); // wykładnik p w i-tej liczbie

      // rozłóż "po 1" wylosowaną ścieżką, żeby uniknąć skupisk
      let left = totalExp;
      while (left > 0) {
        // wybierz indeks preferując mniejsze aktualne wykładniki (żeby różnicować)
        const idxs = [...Array(n).keys()];
        idxs.sort((i, j) => buckets[i] - buckets[j] || Math.random() - 0.5);
        const i = idxs[Math.floor(Math.random() * Math.min(n, 3))]; // wybierz z 3 najmniejszych
        buckets[i]++;
        left--;
      }

      // lekki shuffle
      for (let i = n - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const t = buckets[i]; buckets[i] = buckets[j]; buckets[j] = t;
      }

      bucketsByPrime[p] = buckets;
    }

    // zbuduj liczby
    const candidate = Array(n).fill(1);
    for (let i = 0; i < n; i++) {
      for (const p of primes) {
        const e = bucketsByPrime[p][i];
        if (e > 0) candidate[i] *= powInt(p, e);
      }
    }

    // sanity: produkt musi być dokładnie k^n
    const prod = candidate.reduce((a,b)=>a*b,1);
    if (prod !== prodTarget) continue;

    // warunki powtórzeń: preferuj wszystkie różne; jeśli nie wyjdzie — max x2; nigdy nie wszystkie równe
    const freq = {};
    for (const v of candidate) freq[v] = (freq[v] || 0) + 1;
    const counts = Object.values(freq);
    const maxCount = Math.max(...counts);
    const allEqual = counts.length === 1;

    if (allEqual) continue;                // odrzuć „same takie same”
    if (maxCount > 2) continue;            // żadna wartość nie może wystąpić >2 razy

    // jeśli są powtórzenia x2, spróbuj je od razu rozbić minimalną korektą (przeniesienie najmniejszego czynnika)
    if (maxCount === 2) {
      // poszukaj pary identycznych i przenieś najmniejszy czynnik z jednej do innej liczby
      const idxByVal = {};
      candidate.forEach((v, i) => { (idxByVal[v] ||= []).push(i); });
      let fixed = false;
      for (const [valStr, idxs] of Object.entries(idxByVal)) {
        const val = Number(valStr);
        if (idxs.length === 2) {
          // wybierz najmniejszy dzielnik > 1 (jeśli istnieje)
          let d = 2;
          while (d <= val && val % d !== 0) d++;
          if (d < val) {
            const from = idxs[0];
            // cel: inny indeks, który nie zrówna się do istniejącej wartości
            const candIdx = [...Array(n).keys()].filter(t => !idxs.includes(t));
            if (candIdx.length) {
              const to = candIdx[Math.floor(Math.random()*candIdx.length)];
              candidate[from] /= d;
              candidate[to] *= d;
              fixed = true;
              break;
            }
          }
        }
      }
      if (fixed) {
        // sprawdź ponownie warunki po korekcie
        const prod2 = candidate.reduce((a,b)=>a*b,1);
        if (prod2 !== prodTarget) continue;
        const freq2 = {};
        candidate.forEach(v => freq2[v] = (freq2[v] || 0) + 1);
        const max2 = Math.max(...Object.values(freq2));
        const allEq2 = Object.values(freq2).length === 1;
        if (allEq2 || max2 > 2) continue;
      }
    }

    arr = candidate;
    break;
  }

  // awaryjnie — gdyby losowanie się uparło — weź różne potęgi k (1, k, k^2, ...), dociągając sumą wykładników do n
  if (!arr) {
    const exps = Array(n).fill(0);
    // rozdaj n „jednostek” wykładników tak, by powstały różne wartości
    for (let i = 0; i < n; i++) exps[i] = i;   // 0,1,2,3,4
    let sum = exps.reduce((a,b)=>a+b,0);
    while (sum > n) { // jeśli przekroczyliśmy, obniżaj najwyższe
      const j = exps.indexOf(Math.max(...exps));
      exps[j]--; sum--;
    }
    while (sum < n) { // jeśli brakuje, podnoś najniższe nie tworząc duplikatów
      const j = exps.indexOf(Math.min(...exps));
      exps[j]++; sum++;
    }
    // zbuduj liczby
    arr = exps.map(e => powInt(k, e));
    // upewnij się, że produkt OK
    const prod = arr.reduce((a,b)=>a*b,1);
    if (prod !== prodTarget) {
      // ostatecznie: [1, k, ..., k^{n-1}] i ostatnią podmień na taką, by suma wykładników = n
      const base = exps.map((_,i)=>i);
      const need = n - base.reduce((a,b)=>a+b,0);
      base[n-1] += need;
      arr = base.map(e => powInt(k, e));
    }
  }

  return {
    type: "MeanGeometricRawTask",
    data: { n, arr, prod: prodTarget, g: k, k },
    uid: `mean_geo_int_${Date.now()}_${Math.floor(Math.random()*1e9)}`
  };
}


export function renderTask({ data }) {
  const listLatex = data.arr.join(",\\,");
  return (
    <div style={{ textAlign:"left", margin:"8px 0 12px", lineHeight:1.6 }}>
      <div>
        <span>Podane liczby: </span>
        <InlineMath math={listLatex} />
        <span>.</span>
      </div>
      <div style={{ marginTop: 4 }}>
        <span>Oblicz </span><InlineMath math={'g'} /><span>, czyli <b>średnią geometryczną</b> tych liczb.</span>
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3">
      <span><InlineMath math={'g = '} /></span>
      <FractionInput value={userAnswer} onChange={onChange} disabled={disabled} label="g" placeholder="np. 8" />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || !userAnswer.raw) return { correct:false, message:"Podaj wartość g." };
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct:false, message:"Nieprawidłowy zapis." };
  const uv = parsed[0] / parsed[1];
  const correct = Math.abs(uv - data.g) <= 0.01 + 1e-12;
  return { correct, expected: `${data.g}` };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`g = ${data.g}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw, 'g')} />;
}

export function renderExplanation({ data }) {
  const prodStr = data.arr.join("\\cdot\\,");
  return (
    <div style={{textAlign:"left", lineHeight:1.75}}>
      <div><strong>Krok 1 — iloczyn</strong></div>
      <BlockMath math={`${prodStr} = ${data.prod} = ${data.k}^{${data.n}}`} />
      <div><strong>Krok 2 — średnia geometryczna</strong></div>
      <BlockMath math={`g = \\sqrt[${data.n}]{${prodStr}} = \\sqrt[${data.n}]{${data.k}^{${data.n}}} = ${data.k}`} />
    </div>
  );
}
